import { Button } from '@progress/kendo-react-buttons';
import { formatNumber } from '@progress/kendo-intl';
import { SvgIcon } from "@progress/kendo-react-common";
import { Grid, GridCustomCellProps, GridColumn as Column, GridEvent, GridToolbar } from '@progress/kendo-react-grid';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { arrowRotateCwIcon, downloadIcon, fileIcon, filterClearIcon, inheritedIcon, trackChangesAcceptIcon } from '@progress/kendo-svg-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import CenterDivPanel from '../../../components/CenterDivPanel';
import { downloadBlobAsFile, fetchApi, fetchByteData } from '../../../services/api';
import { openWindowPerc } from '../../../services/openWindow';
import { InvoiceEmailReviewDialog } from '../EmailInvoices/DetailRow/InvoiceEmailReviewDialog';
import { TotalChargesCell } from '../TotalChargesCell';
import { InvoiceCustomerSummary, InvoiceDetail, PostDataFilter, PostDataState } from './InvoiceGateway';
import { RightAlignHeaderCell } from 'components/cells/RightAlignHeaderCell';

type props = {
  Filter: PostDataFilter,
  InvoiceCustomerSummary: InvoiceCustomerSummary
};

interface InvoiceDetailCommandCellProps extends GridCustomCellProps {
  sendInvoice: (item: InvoiceDetail) => void;
  showIdentificationRecords: (item: InvoiceDetail) => void;
}

const InvoiceDetailCommandCell = (props: InvoiceDetailCommandCellProps) => {
  const dataItem: InvoiceDetail = props.dataItem;

  return (
    <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
      <Button themeColor={"primary"} onClick={() => props.sendInvoice(dataItem)}>Send Invoice</Button>
      {/*<SplitButton text="Send Invoice" themeColor={"primary"} items={[{ text: 'Set Identification Records' }]}*/}
      {/*  onButtonClick={() => props.sendInvoice(dataItem)}*/}
      {/*  onItemClick={(e) => {*/}
      {/*    e.item.text === 'Set Identification Records' && props.showIdentificationRecords(dataItem)*/}
      {/*  }} />*/}
    </td>
  );
};

const PageSize = 175;

const InvoiceGatewayInvoiceDetailComponent = (props: props) => {
  const [loading, setLoading] = useState(false);
  const render = useRef(true);

  const initialPropsPostDataState = {
    skip: 0,
    take: PageSize,
    filters: { ...props.Filter, PostBatchID: props.InvoiceCustomerSummary.InvoicePostBatchID, CustomerID: props.InvoiceCustomerSummary.CustomerID, UsesInvoiceEmailModule: props.InvoiceCustomerSummary.InvoiceEmailRecord }
  } as PostDataState

  const defaultPostDataState = {
    skip: 0,
    take: PageSize,
    filters: {
      ...props.Filter, PostBatchID: props.InvoiceCustomerSummary.InvoicePostBatchID, CustomerID: props.InvoiceCustomerSummary.CustomerID, UsesInvoiceEmailModule: props.InvoiceCustomerSummary.InvoiceEmailRecord,
      OrderNumber: null
    }
  } as PostDataState

  const [postDataState, setPostDataState] = useState<PostDataState>({ ...initialPropsPostDataState });

  const [totalRecords, setTotalRecords] = useState(0);

  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetail[]>([]);

  const fetchMain = useCallback((append: boolean, _postDataState: PostDataState = null) => {
    //if (props.InvoiceCustomerSummary.Expanded === false)
    //  return;
    setLoading(true);

    const dataGridState = _postDataState == null ? { ...postDataState } : { ..._postDataState };
    if (!append) {
      dataGridState.take = PageSize;
      dataGridState.skip = 0;

      //document.querySelector('.InvoiceGatway-andFilter .k-grid-content').scrollTop = 0;
      setPostDataState(dataGridState);
    }

    fetchApi(`/api/Billing/GetInvoiceGatewayInvoices`, dataGridState, 'POST')
      .then(({ Data, Total }) => {
        setTotalRecords(Total);
        if (!append) {
          setInvoiceDetail(Data);
        } else {
          setInvoiceDetail(prevData => prevData.concat(Data));
        }
        setLoading(false);
      }).catch(async e => {
        setLoading(false);
        if (!e?.status)
          alert('Error: Please see admin');
        else if (e.status !== 404) {
          alert(e?.detail);
        }
      });
  }, [postDataState]);

  useEffect(() => {
    if (props.InvoiceCustomerSummary.Expanded === false)
      return;

    if (render.current) {
      render.current = false;
    } else {
      return;
    }
    fetchMain(false);
  }, [fetchMain, postDataState, props.InvoiceCustomerSummary.Expanded]);

  const search = useDebouncedCallback(useCallback((o: PostDataState) => {
    fetchMain(false, o);
  }, [fetchMain]), 750);

  const SaveAndFetchPostDataState = (o: PostDataState) => {
    setPostDataState(o);
    search(o);
  }

  const scrollHandler = (event: GridEvent) => {
    const scrollElement = event.syntheticEvent.currentTarget as HTMLElement;
    const isNearBottom = scrollElement.scrollHeight > scrollElement.clientHeight
      && scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10;

    if (!isNearBottom || loading || invoiceDetail.length >= totalRecords)
      return;

    postDataState.skip = invoiceDetail.length;
    fetchMain(true);
  }
  const markAsAccepted = (dataItem: InvoiceDetail) => {

    setLoading(true);

    const data = {
      PostBatchNumber: dataItem.InvoicePostBatchID,
      InvoiceID: dataItem.InvoiceID,
      CustomerID: dataItem.CustomerID,
      LocationID: dataItem.LocationID,
      APICredID: dataItem.APICredID,
      Status: 100, //accepted //todo add enum
      InvoiceStatusRecordType: postDataState.filters?.InvoiceStatusRecordType,
      InvoiceEmailRecord: dataItem.InvoiceEmailRecord
    };

    fetchApi(`/api/billing/UpdateInvoiceGatewayInvoiceStatus`, data, 'POST')
      .then((resp: any) => {
        dataItem.InvoiceStatusName = resp.StatusName;
        dataItem.InvoiceStatus = resp.Status;
      }).catch(async e => {
        if (!e?.status)
          alert('Error: Please see admin');
        else if (e.status !== 404) {
          alert(e?.detail);
        }
      }).finally(() => setLoading(false));
  }

  const fetchInvoiceGatewayOrderPoints = (props: InvoiceDetail) => {
    setLoading(true);
    fetchApi(`/api/Billing/InvoiceGatewayOrderPoints/${props.OrderID}`)
      .then((response: any) => {
        props.InvoiceGatewayOrderPoints = response.InvoiceGatewayOrderPoints;
        // setInvoiceBatchSummary([...invoiceBatchSummary]);

        setLoading(false);
      })
      .catch(e => {
        if (!e?.status)
          alert('Error: Please see admin');
        else if (e.status !== 404) {
          alert(e?.detail);
        }
        setLoading(false);
      });
  }

  const downloadEDIFile = (dataItem: InvoiceDetail) => {
    setLoading(true);
    fetchByteData(`/api/Billing/DownloadEDIFile/${dataItem.InvoiceID}`, {}, 'POST')
      .then((resp) => {
        downloadBlobAsFile(resp.blob, resp.filename);
        setLoading(false);
      })
      .catch(e => {
        console.log(e);
        if (!e?.status)
          alert('Error: Please see admin');
        else if (e.status !== 404 && e.status !== 400) {
          alert(e?.detail);
        }
        setLoading(false);
      });
  }

  const setAsInProgress = (dataItem: InvoiceDetail) => {

    setLoading(true);

    const data = {
      InvoiceID: dataItem.InvoiceID,
      PostBatchNumber: dataItem.InvoicePostBatchID,
      CustomerID: dataItem.CustomerID,
      LocationID: dataItem.LocationID,
      APICredID: dataItem.APICredID,
      Status: 19, //processing (generic), server will perform the switch  //todo add enum
      InvoiceStatusRecordType: postDataState.filters?.InvoiceStatusRecordType,
      InvoiceEmailRecord: dataItem.InvoiceEmailRecord
    };

    fetchApi(`/api/billing/UpdateInvoiceGatewayInvoiceStatus`, data, 'POST')
      .then((resp: any) => {
        dataItem.InvoiceStatusName = resp.StatusName;
        dataItem.InvoiceStatus = resp.Status;
        dataItem.InvoiceSentStatusComment = '';
      }).catch(async e => {
        if (!e?.status)
          alert('Error: Please see admin');
        //else if (e.status !== 404) {
        //  alert(e?.detail);
        //}
      }).finally(() => setLoading(false));
  }

  const InvoiceNumberCell = (props: GridCustomCellProps) => {
    if (!props.field)
      return null;

    const dataItem: InvoiceDetail = props.dataItem;

    return (
      <td {...props.tdProps}>
        <a href="#" style={{ color: "#338aff" }} tabIndex={-1} onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Order/${dataItem.OrderID}/Rate/${dataItem.InvoiceSequence}/${dataItem.InvoiceLetter}`, .9, .85);
        }}>{dataItem.InvoiceNumber}</a>

        <a href="#" style={{ color: "#338aff" }} tabIndex={-1} className="ml-2" title='Invoice View' onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Invoice/View/${dataItem.OrderID}/${dataItem.InvoiceLetter}`, .9, .85);
        }}><SvgIcon icon={fileIcon} size="xsmall" /></a>
      </td>
    );
  };

  const SentStatusCell = (props: GridCustomCellProps) => {
    if (!props.field)
      return null;

    const dataItem: InvoiceDetail = props.dataItem;

    return (
      <td {...props.tdProps}>
        <div className="container">
          <div className="row" style={{ alignItems: 'center' }}>
            <div className="col-3 pl-0" style={{ whiteSpace: 'nowrap' }}>
              <Tooltip position={'bottom'}>
                <div title={dataItem.InvoiceSentStatusComment}>
                  {dataItem.InvoiceStatusName}
                </div>
              </Tooltip>
              {dataItem.InvoiceStatusName === 'Failed' && <div style={{ fontSize: 'smaller' }}>
                <em>{dataItem.InvoiceSentStatusComment}</em>
              </div>}
            </div>
            {dataItem.InvoiceStatus !== 100 && <div className="col-auto pl-2">
              <Button size={'small'} themeColor={"primary"} fillMode="outline" style={{ fontSize: 12 }} onClick={() => markAsAccepted(dataItem)}>
                Mark as Accepted
              </Button>
            </div>}
          </div>
        </div>
      </td>
    );
  };

  const ShowAllReferenceNumbers = (dataItem: InvoiceDetail) => {
    dataItem.ShowAllReferenceNumbers = !dataItem.ShowAllReferenceNumbers;
    setInvoiceDetail([...invoiceDetail]);
  }

  const ReferenceNumberCell = (props: GridCustomCellProps) => {
    if (!props.field)
      return null;

    const dataItem: InvoiceDetail = props.dataItem;
    return (
      <td {...props.tdProps}>
        <div><a href="#" style={{ color: "#338aff" }} tabIndex={-1} onClick={(e) => { e.preventDefault(); ShowAllReferenceNumbers(dataItem); }}>{dataItem.ReferenceNumber1}</a></div>
        {
          dataItem.ShowAllReferenceNumbers && <div>
            <div>{dataItem.ReferenceNumber2}</div>
            <div>{dataItem.ReferenceNumber3}</div>
            {dataItem.ReferenceNumber4 && <div>{dataItem.ReferenceNumber4}</div>}
          </div>
        }
      </td >
    );
  };

  const EntityIdentificationCell = (props: GridCustomCellProps) => {
    const [showMenu, setShowMenu] = useState<boolean>(false);

    if (!props.field)
      return null;

    const dataItem: InvoiceDetail = props.dataItem;

    if (dataItem.LocationID > 0) {
      return (
        <td {...props.tdProps}>
          <div>
            {!dataItem.InvoiceGatewayOrderPoints
              ?
              <a href="#" style={{ color: "#338aff" }} tabIndex={-1} onClick={(e) => { e.preventDefault(); fetchInvoiceGatewayOrderPoints(dataItem); }} title='Show Entity Identification Recs.' className="mr-1"><SvgIcon icon={inheritedIcon} /></a>
              :
              <div>
                <div><a href="#" style={{ color: "#338aff" }} tabIndex={-1} onClick={(e) => { e.preventDefault(); openWindowPerc(`/edi/updateedientityidentification/${dataItem.LocationID}/${dataItem.InvoiceGatewayOrderPoints.ShipperCustomerID}`, .5, .70); }}>PU</a>: {dataItem.InvoiceGatewayOrderPoints.ShipperNumber}-{dataItem.InvoiceGatewayOrderPoints.ShipperName}</div>
                <div><a href="#" style={{ color: "#338aff" }} tabIndex={-1} onClick={(e) => { e.preventDefault(); openWindowPerc(`/edi/updateedientityidentification/${dataItem.LocationID}/${dataItem.InvoiceGatewayOrderPoints.ConsigneeCustomerID}`, .5, .70); }}>DR</a>: {dataItem.InvoiceGatewayOrderPoints.ConsigneeNumber}-{dataItem.InvoiceGatewayOrderPoints.ConsigneeName}</div>
                <div><a href="#" style={{ color: "#338aff" }} tabIndex={-1} onClick={(e) => { e.preventDefault(); openWindowPerc(`/edi/updateedientityidentification/${dataItem.LocationID}/${dataItem.InvoiceGatewayOrderPoints.BillToCustomerID}/${dataItem.InvoiceID}`, .5, .70); }}>BT</a>: {dataItem.InvoiceGatewayOrderPoints.BillToNumber}-{dataItem.InvoiceGatewayOrderPoints.BillToName}</div>
              </div>
            }
            {(dataItem.InvoiceStatusName === 'Sent' || dataItem.InvoiceStatusName === 'Accepted')
              && <a href="#" style={{ color: "#338aff" }} tabIndex={-1} onClick={(e) => { e.preventDefault(); downloadEDIFile(dataItem); }} title='Download EDI File'><SvgIcon icon={downloadIcon} /></a>}
          </div>
        </td>
      );
    }

    if (dataItem.InvoiceEmailRecord) {
      return (
        <td {...props.tdProps}>
          {showMenu &&
            <InvoiceEmailReviewDialog
              InvoiceID={dataItem.InvoiceID}
              CustomerID={dataItem.CustomerID}
              InvoiceNumber={dataItem.InvoiceNumber}
              CustomerName={dataItem.CustomerName}
              CloseDialog={() => setShowMenu(false)}
            />
          }
          <a
            href="#" style={{ color: "#338aff" }} tabIndex={-1}
            onClick={(e) => {
              e.preventDefault();
              setShowMenu(true);
            }}
          >
            <SvgIcon icon={trackChangesAcceptIcon} />
          </a>
        </td>
      );
    }

    return (
      <td {...props.tdProps}>
        <div>-</div>
      </td>
    );
  };

  return (<div className="" style={{ position: "relative" }}>
    {loading && <CenterDivPanel>
      <div className="k-loading-image" />
    </CenterDivPanel>}
    <Grid
      //className='InvoiceGatway-andFilter'
      scrollable="scrollable"
      style={{
        maxHeight: `490px`,
      }}
      data={invoiceDetail}
      onScroll={scrollHandler}
      resizable={true}
    >
      <GridToolbar size="small">
        <Button
          icon="refresh"
          svgIcon={arrowRotateCwIcon}
          title="Refresh"
          size="small"
          onClick={() => fetchMain(false)}
        />

        <NumericTextBox
          value={postDataState.filters?.OrderNumber}
          placeholder='Order Number'
          min={0}
          spinners={false}
          width={130}
          format="0"
          onChange={(e) => {
            SaveAndFetchPostDataState({ ...postDataState, filters: { ...postDataState.filters, OrderNumber: e?.value } });
          }}
        />

        <Button
          title="Clear All Filters and Sort"
          icon="filter-clear"
          svgIcon={filterClearIcon}
          onClick={() => {
            SaveAndFetchPostDataState({ ...defaultPostDataState });
          }}
        />
      </GridToolbar>
      <Column field="InvoiceNumber" title="Invoice Number" width="120px" cells={{ data: InvoiceNumberCell }} />
      <Column field="InvoiceStatus" title="Sent Status" cells={{ data: SentStatusCell }} />
      <Column field="ReferenceNumber1" title="Reference Number" cells={{ data: ReferenceNumberCell }} />
      <Column field="ReferenceNumber1" title="Entity Identification/Options" cells={{ data: EntityIdentificationCell }} />
      <Column field="TotalCharges" title="Total Charges" cells={{ headerCell: RightAlignHeaderCell, data: TotalChargesCell }} />
      <Column width={110} cells={{ data: (props: GridCustomCellProps) => <InvoiceDetailCommandCell {...props} sendInvoice={(e) => { setAsInProgress(e); }} showIdentificationRecords={(e) => { alert(e.CustomerName); }} /> }} />
    </Grid>
    {/*</InvoiceGatewayContext.Provider>*/}
    {
      totalRecords > 0 &&
      <div className="k-pager k-pager-md k-grid-pager">
        <div style={{ marginLeft: "auto", marginRight: 0 }}>
          {formatNumber(Math.min(invoiceDetail.length, totalRecords), 'n0')} of {formatNumber(totalRecords, 'n0')} items
        </div>
      </div>
    }
  </div>);
}

export default InvoiceGatewayInvoiceDetailComponent;
